import { computed, onUnmounted, ref } from 'vue';
import {
    CacheBustImage,
    CustomizerModel,
    CustomizerTabFacet,
    CustomizerTabModel,
    CustomizerTabType, MaterialAndColorCustomizerModel,
    ProductClientService,
    ProductSpotModel,
    ComfortCustomizerModel,
} from '@/types/productServerApi';
import useProductDetailData from '@/shared/product/details/useProductDetailData';
import utilities from '@/shared/utilities.service';
import generateCdnUrlWithParams from '@/core/responsive/image/helpers/generate-cdn-url-including-params';
import { ImageFit } from '@/core/responsive/image/image.types';
import scrollService from '@/core/scroll/scroll.service';
import { useRouter } from 'vue-router';

export interface ActiveCustomizerOverlay {
    id: string;
    type: CustomizerTabType;
}

const activeCustomizerOverlay = ref<ActiveCustomizerOverlay | null>(null);

export default function useCustomizer() {
    const router = useRouter();
    const { productData, variantId } = useProductDetailData();

    const customizer = computed<CustomizerModel>(() => productData.value.customizerModel ?? {} as CustomizerModel);
    const tabs = computed<CustomizerTabModel[]>(() => customizer.value?.tabs ?? [] as CustomizerTabModel[]);

    const getProductFamilyCustomizerTabFacet = async(tabId: string): Promise<CustomizerTabFacet> => {
        const result = await ProductClientService.getProductFamilyCustomizerTabFacet(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return result ?? {} as CustomizerTabFacet;
    };

    const getDefaultCustomizerTabFacet = async(tabId: string): Promise<CustomizerTabFacet> => {
        const result = await ProductClientService.getDefaultCustomizerTabFacet(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return result ?? {} as CustomizerTabFacet;
    };

    const getMaterialAndColorCustomizerTabFacet = async(tabId: string): Promise<CustomizerTabFacet> => {
        const result = await ProductClientService.getMaterialAndColorCustomizerTabFacet(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return result ?? {} as CustomizerTabFacet;
    };

    const getComfortCustomizerTabFacet = async(tabId: string): Promise<CustomizerTabFacet> => {
        const result = await ProductClientService.getComfortCustomizerTabFacet(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return result ?? {} as CustomizerTabFacet;
    };

    const getLegsCustomizerTabFacet = async(tabId: string): Promise<CustomizerTabFacet> => {
        const result = await ProductClientService.getLegsCustomizerTabFacet(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return result ?? {} as CustomizerTabFacet;
    };

    const getProductFamilyCustomizers = async(tabId: string): Promise<ProductSpotModel[]> => {
        const response = await ProductClientService.getProductFamilyCustomizers(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return response?.results ?? [] as ProductSpotModel[];
    };

    const getProductComfortCustomizer = async(tabId: string): Promise<ComfortCustomizerModel[]> => {
        const response = await ProductClientService.getProductComfortCustomizer(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return response ?? [] as ComfortCustomizerModel[];
    };

    const getMaterialAndColorCustomizer = async(tabId: string): Promise<MaterialAndColorCustomizerModel> => {
        const response = await ProductClientService.getMaterialAndColorCustomizer(variantId.value, customizer.value.id, tabId)
            .then(utilities.unwrapModel);
        return response ?? {} as MaterialAndColorCustomizerModel;
    };

    const getMaterialImageUrl = (image?: CacheBustImage): string | undefined => {
        const options = {
            background: 'transparent',
            format: 'auto',
            quality: '33',
            // focus: '1000,1000,1320,1160',
            fit: 'crop' as ImageFit,
            width: 400,
            height: 320,
            cacheBust: image?.cacheBust ?? 0,
        };
        return image ? generateCdnUrlWithParams(image?.path ?? '', options, true) : undefined;
    };

    const setActiveCustomizerOverlay = (id: string, type: CustomizerTabType) => {
        activeCustomizerOverlay.value = { id, type };
    };

    const clearActiveCustomizerOverlay = (scrollToTop: boolean = false) => {
        activeCustomizerOverlay.value = null;
        scrollToTop && scrollService.smoothScroll(0);
    };

    const activeCustomizerOverlayId = computed<string>(() => activeCustomizerOverlay.value?.id ?? '');

    const activeCustomizerOverlayType = computed<CustomizerTabType| undefined>(() => activeCustomizerOverlay.value?.type);

    const unRegisterNavigationGuard = router.beforeEach((to, from, next): void => {
        clearActiveCustomizerOverlay();
        next(true);
    });

    onUnmounted(() => {
        unRegisterNavigationGuard();
    });

    return {
        customizer,
        tabs,
        activeCustomizerOverlay,
        activeCustomizerOverlayId,
        activeCustomizerOverlayType,
        setActiveCustomizerOverlay,
        clearActiveCustomizerOverlay,
        getProductFamilyCustomizerTabFacet,
        getMaterialAndColorCustomizerTabFacet,
        getDefaultCustomizerTabFacet,
        getComfortCustomizerTabFacet,
        getLegsCustomizerTabFacet,
        getProductFamilyCustomizers,
        getMaterialAndColorCustomizer,
        getMaterialImageUrl,
        getProductComfortCustomizer,
    };
}
